import React, { useState } from "react"
import { Modal, Form, Input, Button, Typography, Flex, Radio, DatePicker, InputNumber, Table, Switch, Space } from "antd"
import { SyncOutlined } from "@ant-design/icons"
import { UnsavedChangesModal } from "ui/UnsavedChangesModal"
import ExperienceSelector from "pagesV2/pro/Equipment/experience/ExperienceSelector"
import PropTypes from "prop-types"
import dayjs from "dayjs"
import { useTranslation } from "react-i18next"
import styles from "./index.module.css"
import { DISCOUNT_TYPE } from "./constants"
import { useDiscountFormData } from "./useDiscountFormData"
import { useDiscountCodeCheck } from "./useDiscountCodeCheck"
import { getExperienceColumns } from "./experienceColumns"

const { Title } = Typography
const { RangePicker } = DatePicker

const DiscountFormModal = ({ visible, onCancel, onSave, initialValues, isSaving }) => {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const [showUnsavedModal, setShowUnsavedModal] = useState(false)
  const [hasErrors, setHasErrors] = useState(false)
  const discountType = Form.useWatch("discount_type", form)
  const isEditMode = Boolean(initialValues)

  const { experiences, selectedExperienceData, handleRemoveExperience } = useDiscountFormData({ form, visible, initialValues })
  const { isCheckingCode, handleCodeBlur, handleCodeChange, generateCode, duplicateCodeValidator } = useDiscountCodeCheck({ form, isEditMode, initialCode: initialValues?.code })

  const valueLabel = discountType === DISCOUNT_TYPE.PERCENTAGE
    ? t("settings.discount_form_value_label_pct")
    : t("settings.discount_form_value_label_fixed")

  const experienceColumns = getExperienceColumns({ t, onRemove: handleRemoveExperience })

  const requestCancel = () => {
    if (form.isFieldsTouched()) {
      setShowUnsavedModal(true)
    } else {
      executeCancel()
    }
  }

  const executeCancel = () => {
    form.resetFields()
    setShowUnsavedModal(false)
    onCancel()
  }

  const handleFinish = (values) => {
    const [validFrom, validUntil] = values.validity ?? [null, null]
    const allSelected = experiences.length > 0 && values.applicable_experiences?.length === experiences.length
    const { apply_to_add_ons, ...rest } = values
    onSave({
      ...rest,
      applicable_experiences: allSelected ? "all" : values.applicable_experiences.map(Number),
      valid_from: validFrom ? validFrom.format("YYYY-MM-DD") : null,
      valid_until: validUntil ? validUntil.format("YYYY-MM-DD") : null,
      validity: undefined,
      usage_cap: values.usage_cap || null,
      per_user_usage_cap: values.per_user_usage_cap || null,
      applicability_rules: {
        apply_to_add_ons: apply_to_add_ons ?? true
      }
    })
  }

  return (
    <Modal
      open={visible}
      onCancel={requestCancel}
      footer={
        <Flex justify="end" gap={8}>
          <Button onClick={requestCancel} color="primary" variant="outlined" disabled={isSaving}>
            {t("global.cancel")}
          </Button>
          <Button
            color="primary"
            variant="solid"
            loading={isSaving}
            disabled={hasErrors}
            onClick={() => form.submit()}
          >
            {isEditMode ? t("global.save") : t("settings.discount_form_submit_new")}
          </Button>
        </Flex>
      }
      width={600}
      maskClosable={false}
      centered
      destroyOnClose
      title={<Title level={4} style={{ margin: 0 }}>{isEditMode ? t("settings.discount_form_title_edit") : t("settings.discount_form_title_new")}</Title>}
      classNames={{ body: styles.modalBody }}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        onFieldsChange={(_, allFields) => setHasErrors(allFields.some((f) => f.errors?.length > 0))}
      >
        <Form.Item
          name="name"
          label={t("settings.discount_form_name_label")}
          rules={[{ required: true, message: t("settings.discount_form_name_required") }]}
        >
          <Input placeholder="e.g., Summer Sale" size="large" />
        </Form.Item>

        <Form.Item
          label={t("settings.discount_form_code_label")}
          required
          rules={[
            { required: true, message: t("settings.discount_form_code_required") },
            { pattern: /^[A-Z0-9_-]+$/i, message: t("settings.discount_form_code_invalid") }
          ]}
        >
          <Flex gap={8}>
            <Form.Item
              name="code"
              noStyle
              rules={[
                { required: true, message: t("settings.discount_form_code_required") },
                { pattern: /^[A-Z0-9_-]+$/i, message: t("settings.discount_form_code_invalid") },
                { validator: duplicateCodeValidator }
              ]}
            >
              <Input
                placeholder="e.g., SUMMER20"
                size="large"
                style={{ textTransform: "uppercase", flex: 1 }}
                onChange={handleCodeChange}
                onBlur={handleCodeBlur}
                disabled={isCheckingCode}
                suffix={isCheckingCode ? <SyncOutlined spin /> : null}
              />
            </Form.Item>
            <Button size="large" icon={<SyncOutlined />} onClick={generateCode}>
              {t("settings.discount_form_generate")}
            </Button>
          </Flex>
        </Form.Item>

        <Form.Item name="discount_type" label={t("settings.discount_form_type_label")}>
          <Radio.Group onChange={() => form.validateFields(["value"])}>
            <Radio value={DISCOUNT_TYPE.PERCENTAGE}>{t("settings.discount_type_percentage")}</Radio>
            <Radio value={DISCOUNT_TYPE.FIXED_AMOUNT}>{t("settings.discount_type_fixed")}</Radio>
          </Radio.Group>
        </Form.Item>



        <Form.Item
          name="value"
          label={valueLabel}
          rules={[
            { required: true, message: t("settings.discount_form_value_required") },
            {
              validator: (_, val) => {
                if (val === null || val === undefined || val === "") return Promise.resolve()
                if (val <= 0) return Promise.reject(new Error(t("settings.discount_form_value_gt_zero")))
                if (discountType === DISCOUNT_TYPE.PERCENTAGE && val > 100)
                  return Promise.reject(new Error(t("settings.discount_form_value_max_pct")))
                return Promise.resolve()
              }
            }
          ]}
        >
          <InputNumber
            placeholder="0"
            size="large"
            step={discountType === DISCOUNT_TYPE.PERCENTAGE ? 1 : 0.01}
            style={{ width: "100%" }}
          />
        </Form.Item>

        <Form.Item label={t("settings.discount_form_apply_to_add_ons", "Apply discount to add-ons")}>
          <Space>
            <Form.Item name="apply_to_add_ons" valuePropName="checked" noStyle>
              <Switch />
            </Form.Item>
            <Typography.Text type="secondary">{t("settings.discount_form_apply_to_add_ons_hint", "Discount applies to add-on prices")}</Typography.Text>
          </Space>
        </Form.Item>

        <Form.Item
          name="applicable_experiences"
          label={t("settings.discount_form_experiences_label")}
          rules={[{ required: true, message: "Please select at least one experience" }]}
        >
          <ExperienceSelector experiences={experiences} />
        </Form.Item>

        {selectedExperienceData.length > 0 && (
          <div style={{ maxHeight: 240, overflowY: "auto", marginTop: -16, marginBottom: 24 }}>
            <Table
              dataSource={selectedExperienceData}
              rowKey="id"
              columns={experienceColumns}
              pagination={false}
              size="small"
            />
          </div>
        )}

        <Form.Item name="validity" label={t("settings.discount_form_validity_label")}>
          <RangePicker
            size="large"
            style={{ width: "100%" }}
            format="MMM D, YYYY"
            disabledDate={(date) => date.isBefore(dayjs().startOf("day"))}
          />
        </Form.Item>

        <Form.Item
          name="usage_cap"
          label={t("settings.discount_form_total_cap_label", "Total Redemptions Cap")}
          extra={t("settings.discount_form_total_cap_hint", "Leave empty for unlimited. Maximum number of times this code can be used across all guests.")}
          rules={[
            {
              validator: (_, val) => {
                if (val === null || val === undefined || val === "") return Promise.resolve()
                if (val <= 0) return Promise.reject(new Error(t("settings.discount_form_cap_gt_zero", "Value must be greater than 0")))
                return Promise.resolve()
              }
            }
          ]}
        >
          <InputNumber
            placeholder={t("settings.discount_form_cap_placeholder_unlimited", "Unlimited")}
            size="large"
            style={{ width: "100%" }}
            onChange={() => form.validateFields(["per_user_usage_cap"])}
          />
        </Form.Item>

        <Form.Item
          name="per_user_usage_cap"
          label={t("settings.discount_form_per_guest_cap_label", "Per-Guest Limit")}
          extra={t("settings.discount_form_per_guest_cap_hint", "Leave empty for unlimited. Maximum number of times each guest can use this code.")}
          rules={[
            {
              validator: (_, val) => {
                if (val === null || val === undefined || val === "") return Promise.resolve()
                if (val <= 0) return Promise.reject(new Error(t("settings.discount_form_cap_gt_zero", "Value must be greater than 0")))
                const totalCap = form.getFieldValue("usage_cap")
                if (totalCap && val > totalCap)
                  return Promise.reject(new Error(t("settings.discount_form_per_guest_exceeds_total", "Per-guest limit cannot exceed total redemptions cap")))
                return Promise.resolve()
              }
            }
          ]}
        >
          <InputNumber placeholder={t("settings.discount_form_cap_placeholder_unlimited", "Unlimited")} size="large" style={{ width: "100%" }} />
        </Form.Item>

        <Form.Item name="status" label={t("settings.discount_form_status_label")}>
          <Radio.Group>
            <Radio value="active">{t("settings.status_active")}</Radio>
            <Radio value="inactive">{t("settings.status_inactive")}</Radio>
          </Radio.Group>
        </Form.Item>
      </Form>

      <UnsavedChangesModal
        open={showUnsavedModal}
        onDiscard={executeCancel}
        onKeepEditing={() => setShowUnsavedModal(false)}
        zIndex={1200}
      />
    </Modal>
  )
}

DiscountFormModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  initialValues: PropTypes.shape({
    name: PropTypes.string,
    code: PropTypes.string,
    discount_type: PropTypes.string,
    value: PropTypes.number,
    applicable_experiences: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
    valid_from: PropTypes.string,
    valid_until: PropTypes.string,
    usage_cap: PropTypes.number,
    per_user_usage_cap: PropTypes.number,
    status: PropTypes.string
  }),
  isSaving: PropTypes.bool
}

export default DiscountFormModal
